import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { CostChartWidget } from "../cost-chart-widget";
import { T } from "./cost-chart.i18n";
import type { PortfolioInfo } from "./types";

type CostChartProps = {
  portfolioId: string | null;
  config: {
    style?: string;
    title?: string;
    description?: string;
    displayMode?: string;
    chartType?: string;
    projectionYears?: number;
  };
  language: string;
  chartColors?: string[] | null;
  /** QL-311 — distinguishes "amount missing" from the generic prerequisites message. */
  portfolioInfo: PortfolioInfo;
  /**
   * Phase 2 S5 — engine reported `kind: "snapshot"` for this component on this portfolio.
   * When set AND prerequisites are met but the cost snapshot row is missing, replace
   * the "Cost data not available" fallback with a clear snapshot-missing placeholder
   * pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
};

export function CostChart({
  portfolioId,
  config,
  language,
  chartColors: _chartColors,
  portfolioInfo,
  hasValidationError = false,
}: CostChartProps) {
  const t = pickT(T, language);
  const cost = usePortfolioSnapshot("cost", portfolioId);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);

  if (cost.isLoading || portfolio.isLoading) return null;

  if (!cost.data) {
    if (!portfolio.data) {
      // QL-311 — name the actual missing input instead of the generic
      // "complete the portfolio selection", walking the causes in the
      // empty-state contract order (custom → risk → amount). A custom
      // portfolio is driven by the builder, so neither input gates it.
      if (!portfolioInfo.isCustomPortfolio) {
        if (!portfolioInfo.hasRiskAssessment) {
          return (
            <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
              {t.completeRiskAssessment}
            </div>
          );
        }
        if (!portfolioInfo.hasInvestmentAmount) {
          return (
            <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
              {t.enterInvestmentAmount}
            </div>
          );
        }
        // Every model-path input is provided, so the gap is genuinely about
        // collection configuration — say so instead of the generic prompt.
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.noPortfolioForScore(String(portfolioInfo.riskScore ?? ""))}
          </div>
        );
      }
      return (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noProductsCost}
        </div>
      );
    }
    // S5 — D20 N/A means the !portfolio.data branch above wouldn't trigger validation.
    // Reaching this fallback with hasValidationError=true means prereqs are met but
    // the cost snapshot row is missing (system-bug case).
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notAvailable}
      </div>
    );
  }

  const snapshot =
    typeof cost.data === "string"
      ? (() => {
          try {
            return JSON.parse(cost.data as string);
          } catch {
            return null;
          }
        })()
      : cost.data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 space-y-2">
        <div className="text-sm font-medium text-destructive">{t.unavailable}</div>
        <div className="text-xs text-muted-foreground">{String(snapshot.error)}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <CostChartWidget
        snapshot={snapshot}
        displayMode={(config.displayMode as "rolling" | "calendar") || undefined}
        chartType={(config.chartType as "line" | "bar") || undefined}
        projectionYears={(config.projectionYears as number) || undefined}
        language={language}
      />
    </VisualizationContainer>
  );
}
